import { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, RefreshCw, Download, Terminal } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { beesClient } from '../../api/beesClient.js';
import { toast } from 'react-hot-toast';

interface EnvironmentState {
  secrets: Record<string, {
    status: 'valid' | 'invalid' | 'missing';
    masked?: string;
    lastChecked: string;
  }>;
  dependencies: Record<string, {
    status: 'installed' | 'missing' | 'outdated';
    version?: string;
    lastChecked: string;
  }>;
  services: Record<string, {
    status: 'healthy' | 'unhealthy' | 'unknown';
    lastChecked: string;
  }>;
  lastFullCheck: string;
  ready: boolean;
}

interface EnvironmentResponse {
  success: boolean;
  message: string;
  data: {
    state: EnvironmentState;
    issues: string[];
    ready: boolean;
    summary: {
      totalChecks: number;
      passed: number;
      failed: number;
    };
  };
}

const EnvValidationTab = () => {
  const [envState, setEnvState] = useState<EnvironmentState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [summary, setSummary] = useState<{ totalChecks: number; passed: number; failed: number } | null>(null);
  const [showSecretInputs, setShowSecretInputs] = useState<Record<string, boolean>>({});
  const [secretValues, setSecretValues] = useState<Record<string, string>>({});
  const [maskedSecrets, setMaskedSecrets] = useState<Set<string>>(new Set());
  const [operationLoading, setOperationLoading] = useState<string | null>(null);

  const checkEnvironment = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await beesClient.runEnvBootstrap({ action: 'check' }) as EnvironmentResponse;
      
      if (response.success && response.data) {
        setEnvState(response.data.state);
        setIssues(response.data.issues || []);
        setSummary(response.data.summary);
        
        const missingSecrets = Object.entries(response.data.state.secrets)
          .filter(([_, secret]) => secret.status === 'missing')
          .map(([name]) => name);
        
        const initialMasked = new Set(missingSecrets);
        setMaskedSecrets(initialMasked);
      } else {
        setError(response.message || 'Failed to check environment');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check environment';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const saveSecret = async (secretName: string) => {
    const value = secretValues[secretName];
    if (!value) {
      toast.error('Please enter a value for the secret');
      return;
    }

    setOperationLoading(`save-${secretName}`);
    try {
      const response = await beesClient.runEnvBootstrap({
        action: 'save-secret',
        key: secretName,
        value: value
      });

      if (response.success) {
        toast.success(`Secret ${secretName} saved successfully`);
        setSecretValues(prev => ({ ...prev, [secretName]: '' }));
        setShowSecretInputs(prev => ({ ...prev, [secretName]: false }));
        await checkEnvironment();
      } else {
        toast.error(response.message || `Failed to save secret ${secretName}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to save secret ${secretName}`;
      toast.error(errorMessage);
    } finally {
      setOperationLoading(null);
    }
  };

  const installDependency = async (depName: string) => {
    setOperationLoading(`install-${depName}`);
    try {
      const response = await beesClient.runEnvBootstrap({
        action: 'install-dependency',
        dependency: depName
      });

      if (response.success) {
        toast.success(`Dependency ${depName} installed successfully`);
        await checkEnvironment();
      } else {
        toast.error(response.message || `Failed to install dependency ${depName}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to install dependency ${depName}`;
      toast.error(errorMessage);
    } finally {
      setOperationLoading(null);
    }
  };

  const resetEnvironment = async () => {
    setOperationLoading('reset');
    try {
      const response = await beesClient.runEnvBootstrap({ action: 'reset' });

      if (response.success) {
        toast.success('Environment reset successfully');
        await checkEnvironment();
      } else {
        toast.error(response.message || 'Failed to reset environment');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset environment';
      toast.error(errorMessage);
    } finally {
      setOperationLoading(null);
    }
  };

  const toggleSecretVisibility = (secretName: string) => {
    setMaskedSecrets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(secretName)) {
        newSet.delete(secretName);
      } else {
        newSet.add(secretName);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
      case 'installed':
      case 'healthy':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'missing':
      case 'unhealthy':
        return <AlertCircle size={16} className="text-red-400" />;
      case 'invalid':
      case 'outdated':
      case 'unknown':
        return <AlertCircle size={16} className="text-yellow-400" />;
      default:
        return <AlertCircle size={16} className="text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
      case 'installed':
      case 'healthy':
        return 'text-green-400';
      case 'missing':
      case 'unhealthy':
        return 'text-red-400';
      case 'invalid':
      case 'outdated':
      case 'unknown':
        return 'text-yellow-400';
      default:
        return 'text-slate-400';
    }
  };

  useEffect(() => {
    checkEnvironment();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Environment Validation</h2>
        <p className="text-slate-400 mt-1">Validate environment setup, secrets, and dependencies</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield size={20} className="text-brand-accent" />
              <span>Environment Status</span>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={checkEnvironment} 
                size="sm"
                disabled={loading || operationLoading !== null}
              >
                {loading ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <RefreshCw size={16} className="mr-2" />
                )}
                Check Environment
              </Button>
              <Button 
                onClick={resetEnvironment} 
                size="sm"
                variant="outline"
                disabled={loading || operationLoading !== null}
              >
                {operationLoading === 'reset' ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Terminal size={16} className="mr-2" />
                )}
                Reset
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            {envState?.ready ? (
              <span className="text-green-400">✅ Environment is ready</span>
            ) : (
              <span className="text-red-400">❌ Environment has issues</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-brand-accent" />
              <span className="ml-2 text-slate-400">Checking environment...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={checkEnvironment}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : (
            <>
              {summary && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-brand-accent">{summary.totalChecks}</div>
                    <div className="text-sm text-slate-400">Total Checks</div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">{summary.passed}</div>
                    <div className="text-sm text-slate-400">Passed</div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-red-400">{summary.failed}</div>
                    <div className="text-sm text-slate-400">Failed</div>
                  </div>
                </div>
              )}

              {issues.length > 0 && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
                  <h4 className="text-sm font-medium text-red-400 mb-2">Issues Found:</h4>
                  <ul className="text-sm text-red-300 space-y-1">
                    {issues.map((issue, index) => (
                      <li key={index}>• {issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              {envState && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-4">Secrets</h3>
                    <div className="space-y-3">
                      {Object.entries(envState.secrets).map(([name, secret]) => (
                        <div key={name} className="p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {getStatusIcon(secret.status)}
                              <div>
                                <div className="text-sm font-medium text-brand-text">{name}</div>
                                <div className={`text-xs ${getStatusColor(secret.status)}`}>
                                  {secret.status} {secret.masked && `• ${secret.masked}`}
                                </div>
                              </div>
                            </div>
                            {secret.status === 'missing' && (
                              <Button
                                size="sm"
                                onClick={() => setShowSecretInputs(prev => ({ ...prev, [name]: !prev[name] }))}
                                disabled={operationLoading !== null}
                              >
                                Set Value
                              </Button>
                            )}
                          </div>
                          
                          {showSecretInputs[name] && secret.status === 'missing' && (
                            <div className="mt-4 space-y-3">
                              <div className="relative">
                                <Input
                                  type={maskedSecrets.has(name) ? 'password' : 'text'}
                                  value={secretValues[name] || ''}
                                  onChange={(e) => setSecretValues(prev => ({ ...prev, [name]: e.target.value }))}
                                  placeholder={`Enter ${name} value...`}
                                  className="violett-input pr-10"
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleSecretVisibility(name)}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-brand-accent"
                                >
                                  {maskedSecrets.has(name) ? <Eye size={16} /> : <EyeOff size={16} />}
                                </button>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  size="sm"
                                  onClick={() => saveSecret(name)}
                                  disabled={operationLoading !== null || !secretValues[name]}
                                >
                                  {operationLoading === `save-${name}` ? (
                                    <>
                                      <Loader2 size={14} className="mr-2 animate-spin" />
                                      Saving...
                                    </>
                                  ) : (
                                    'Save'
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setShowSecretInputs(prev => ({ ...prev, [name]: false }))}
                                  disabled={operationLoading !== null}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-4">Dependencies</h3>
                    <div className="space-y-3">
                      {Object.entries(envState.dependencies).map(([name, dep]) => (
                        <div key={name} className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300">
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(dep.status)}
                            <div>
                              <div className="text-sm font-medium text-brand-text">{name}</div>
                              <div className={`text-xs ${getStatusColor(dep.status)}`}>
                                {dep.status} {dep.version && `• v${dep.version}`}
                              </div>
                            </div>
                          </div>
                          {dep.status === 'missing' && (
                            <Button
                              size="sm"
                              onClick={() => installDependency(name)}
                              disabled={operationLoading !== null}
                            >
                              {operationLoading === `install-${name}` ? (
                                <>
                                  <Loader2 size={14} className="mr-2 animate-spin" />
                                  Installing...
                                </>
                              ) : (
                                <>
                                  <Download size={14} className="mr-2" />
                                  Install
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-4">Services</h3>
                    <div className="space-y-3">
                      {Object.entries(envState.services).map(([name, service]) => (
                        <div key={name} className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300">
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(service.status)}
                            <div>
                              <div className="text-sm font-medium text-brand-text">{name}</div>
                              <div className={`text-xs ${getStatusColor(service.status)}`}>
                                {service.status}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EnvValidationTab;
